/**
 * The REST client for a nurse finishing her own registration after accepting
 * her invitation (decision D3) - pacmny-api's /me/onboarding routes.
 *
 * Self-only by construction: every call acts on the signed-in user, and none
 * names a person, a licence or an address. The lookup and the completion ask
 * Nursys about the licence she saved; the enrolment carries only the last four
 * digits of her SSN, the one thing NCSBN needs that is not on file.
 *
 *  - GET  /me/onboarding                       her details, files, last Nursys answer, what is missing
 *  - PUT  /me/onboarding                       save details (a blank never erases a stored value)
 *  - POST /me/onboarding/documents             a licence or insurance file into her slot
 *  - POST /me/onboarding/nursys-lookups        look her own licence up
 *  - POST /me/onboarding/nursys-registrations  enrol her, only after her own "not on the list"
 *  - POST /me/onboarding/completion            verify again server-side and finish
 */

import api from './client';
import { NURSYS_TIMEOUT_MS } from './nursysBridge';
import { tenDigitPhone } from '@/utils/verifyNurseValidation';

/** The fields PUT /me/onboarding saves, in its names. */
export const FORM_FIELDS = [
  'first_name',
  'last_name',
  'phone',
  'date_of_birth',
  'address_line1',
  'address_line2',
  'city',
  'state',
  'postal_code',
  'license_number',
  'license_state',
  'malpractice_carrier',
  'malpractice_starts_on',
  'malpractice_ends_on',
  'malpractice_per_incident_limit',
  'malpractice_aggregate_limit',
];

/** Money fields: "1000000" and "1000000.00" are the same amount, not a change. */
const AMOUNT_FIELDS = ['malpractice_per_incident_limit', 'malpractice_aggregate_limit'];

export const SESSION_ENDED = 'Your session has ended. Please sign in again.';

/**
 * Her registration is already finished - a completion whose answer never
 * reached the page, say. Every step then answers 403 `onboarding_not_required`
 * ("nothing left to set up"), which is true but leaves her on a page she is
 * done with; the page takes her in instead ({@see isAlreadyOnboarded}).
 */
export const ALREADY_ONBOARDED = 'Your registration is already complete. Taking you to your account.';

export function isAlreadyOnboarded(error) {
  return Boolean(error && Number(error.status) === 403 && error.code === 'onboarding_not_required');
}

export const SSN_NOT_GIVEN =
  'Nothing has been submitted. To enrol you for licence monitoring, NCSBN needs the last four digits of your Social Security number.';

/** A cancelled SSN prompt: nothing was asked of NCSBN. */
export class SsnNotGiven extends Error {
  constructor() {
    super(SSN_NOT_GIVEN);
    this.ssnNotGiven = true;
  }
}

/** The form, from GET /me/onboarding's `user`, `license` and `insurance`. */
export function formFromState(data) {
  const d = data || {};
  const user = d.user || {};
  const license = d.license || {};
  const insurance = d.insurance || {};
  const text = (v) => (v === null || v === undefined ? '' : String(v));

  return {
    first_name: text(user.first_name),
    last_name: text(user.last_name),
    phone: text(user.phone),
    date_of_birth: text(user.date_of_birth),
    address_line1: text(user.address_line1),
    address_line2: text(user.address_line2),
    city: text(user.city),
    state: text(user.state),
    postal_code: text(user.postal_code),
    license_number: text(license.license_number),
    license_state: text(license.license_state),
    malpractice_carrier: text(insurance.carrier),
    malpractice_starts_on: text(insurance.starts_on),
    malpractice_ends_on: text(insurance.ends_on),
    // Main's per-incident and annual limits (perIncidentAmnt, annualAggAmnt).
    malpractice_per_incident_limit: text(insurance.per_incident_limit),
    malpractice_aggregate_limit: text(insurance.aggregate_limit),
  };
}

/**
 * What a save sends: the fields she filled in and changed since the last
 * load, never a blank. The phone goes as its ten digits.
 */
export function changedFields(form, saved) {
  const out = {};
  FORM_FIELDS.forEach((field) => {
    const value = form && form[field] !== undefined && form[field] !== null ? String(form[field]).trim() : '';
    const before = saved && saved[field] !== undefined && saved[field] !== null ? String(saved[field]).trim() : '';
    if (value === '' || value === before) return;
    if (AMOUNT_FIELDS.includes(field) && before !== '' && Number(value) === Number(before)) return;
    out[field] = field === 'phone' ? (tenDigitPhone(value) || value) : value;
  });
  if (out.phone && tenDigitPhone(saved && saved.phone) === out.phone) {
    delete out.phone;
  }
  // A licence is its number and its state together: a state saved before
  // any number has nothing to attach to, so a change to either sends both.
  if (out.license_number || out.license_state) {
    ['license_number', 'license_state'].forEach((field) => {
      const value = form && form[field] ? String(form[field]).trim() : '';
      if (value !== '') out[field] = value;
    });
  }
  return out;
}

/**
 * The page's own words for a refused request, or null for the flow's "could
 * not reach" wording: no answer, a rate limit or a 5xx. The API's refusals
 * carry a sentence meant for her (a refused licence status, a licence
 * enrolled for someone else, a missing detail).
 */
export function onboardingRefusal(error) {
  if (error && error.ssnNotGiven) return SSN_NOT_GIVEN;
  if (isAlreadyOnboarded(error)) return ALREADY_ONBOARDED;
  const status = Number(error && error.status);
  // Her enrolment attempts for the day are used up: a 429 that is an answer
  // about her, not the registry being busy, so it is said in the API's words.
  if (status === 429 && error.code === 'nursys_enrolment_attempts_exhausted') return error.message || null;
  if (!status || status === 429 || status >= 500) return null;
  if (status === 401) return SESSION_ENDED;

  return (error && error.message) || null;
}

export function getOnboarding() {
  return api.get('/me/onboarding');
}

export function updateOnboarding(fields) {
  return api.put('/me/onboarding', fields);
}

/** @param {'license'|'insurance'} type */
export function uploadOnboardingDocument(file, type, onProgress) {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('type', type);

  return api.upload('/me/onboarding/documents', formData, { onProgress });
}

/** Resolves to the API's lookup (`outcome`, `licences`, `errors`); rejects with an ApiError. */
export function lookupOwnLicence() {
  return api.post('/me/onboarding/nursys-lookups', {}, { timeout: NURSYS_TIMEOUT_MS });
}

export function enrolOwnLicence(lastFourSsn) {
  return api.post('/me/onboarding/nursys-registrations', { last_four_ssn: lastFourSsn }, { timeout: NURSYS_TIMEOUT_MS });
}

/** Resolves to her refreshed /me. */
export function completeOnboarding() {
  return api.post('/me/onboarding/completion', {}, { timeout: NURSYS_TIMEOUT_MS });
}

export default {
  getOnboarding,
  updateOnboarding,
  uploadOnboardingDocument,
  lookupOwnLicence,
  enrolOwnLicence,
  completeOnboarding,
};
